(ns hai.ui.components.date-picker
  "Framework-agnostic date / date-and-time picker. Pure reagent + Ornament, no
   re-frame and no Radix, so it runs in both the web-app and the (reagent-only)
   browser extension.

   Value is the local selection, timezone-free:

     {:date {:year 2026 :month 6 :day 8}   ; nil / absent = nothing picked
      :time 1020}                          ; minutes since midnight; nil = date-only

   The Date / Date & time toggle simply controls whether `:time` is present.
   Converting the selection to an absolute instant + timezone is the caller's
   job, kept out of here so the component stays reusable."
  {:ornament/prefix "datepicker-"}
  (:require
   [clojure.string :as str]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Date helpers (calendar math via native js/Date in the browser's local zone)
;; ---------------------------------------------------------------------------

(defn- ->js-date [{:keys [year month day]}]
  (js/Date. year (dec month) (or day 1)))

(defn- js-date->parts [d]
  {:year (.getFullYear d) :month (inc (.getMonth d)) :day (.getDate d)})

(defn- today-date []
  (js-date->parts (js/Date.)))

(defn- add-days [date n]
  (let [d (->js-date date)]
    (.setDate d (+ (.getDate d) n))
    (js-date->parts d)))

(defn- this-weekend-date
  "Upcoming Saturday, or today when today is already Saturday."
  []
  (let [dow (.getDay (->js-date (today-date)))]
    (add-days (today-date) (mod (- 6 dow) 7))))

(defn- shift-month [{:keys [year month]} delta]
  (let [idx (+ (* year 12) (dec month) delta)]
    {:year (quot idx 12) :month (inc (mod idx 12))}))

(defn- month-cells
  "Flat seq for the month grid (week starts Monday): leading nils for the days
   before the 1st, then the day numbers 1..n."
  [{:keys [year month]}]
  (let [leading (mod (+ (.getDay (js/Date. year (dec month) 1)) 6) 7)
        days (.getDate (js/Date. year month 0))]
    (concat (repeat leading nil) (range 1 (inc days)))))

(defn- same-date? [a b]
  (and a b
       (= (:year a) (:year b))
       (= (:month a) (:month b))
       (= (:day a) (:day b))))

(defn- month-label [view]
  (.toLocaleDateString (->js-date (assoc view :day 1))
                       "en-US" #js {:month "long" :year "numeric"}))

;; ---------------------------------------------------------------------------
;; Time helpers
;; ---------------------------------------------------------------------------

(def ^:private default-time-minutes (* 9 60))

(def ^:private time-slots (vec (range 0 (* 24 60) 30)))

(defn format-minutes
  "Minutes since midnight as a 12-hour label, e.g. 1020 -> \"5:00 PM\"."
  [minutes]
  (let [h24 (quot minutes 60)
        mm (mod minutes 60)
        am-pm (if (< h24 12) "AM" "PM")
        h12 (let [h (mod h24 12)] (if (zero? h) 12 h))]
    (str h12 ":" (.padStart (str mm) 2 "0") " " am-pm)))

(defn parse-minutes
  "Parse a freeform time string into minutes since midnight, or nil. Accepts
   forms like \"6\", \"6pm\", \"615pm\", \"6:15\", \"6:15 pm\", \"18:00\", \"1815\"."
  [s]
  (let [cleaned (-> (str s) str/lower-case (str/replace #"\s+" ""))
        [_ h mm am-pm] (re-matches #"(\d{1,2})(?::?(\d{2}))?(am|pm)?" cleaned)]
    (when h
      (let [hour (js/parseInt h 10)
            minute (if mm (js/parseInt mm 10) 0)]
        (when (and (<= 0 hour 23) (<= 0 minute 59)
                   (not (and am-pm (> hour 12))))
          (let [hour (cond
                       (and (= am-pm "pm") (< hour 12)) (+ hour 12)
                       (and (= am-pm "am") (= hour 12)) 0
                       :else hour)]
            (+ (* hour 60) minute)))))))

;; ---------------------------------------------------------------------------
;; Styles
;; ---------------------------------------------------------------------------

(o/defstyled frame :div
  {:display "flex" :flex-direction "column" :gap "12px"
   :color "#2D3E2A"
   :font-size "13px"
   :-webkit-tap-highlight-color "transparent"}

  [:.dp-toggle
   {:display "inline-flex" :align-self "flex-start"
    :gap "2px" :padding "2px"
    :background "#E8EADF" :border-radius "8px"}]
  [:.dp-toggle-opt
   {:padding "5px 12px"
    :border "none" :background "transparent" :border-radius "6px"
    :font-family "inherit" :font-size "12px" :font-weight "500"
    :color "#6B7560" :cursor "pointer"}
   [:&.active
    {:background "white" :color "#2D3E2A"
     :box-shadow "0 1px 2px rgba(45,62,42,0.12)"}]]

  [:.dp-chips
   {:display "flex" :flex-wrap "wrap" :gap "8px"}]
  [:.dp-chip
   {:padding "5px 12px"
    :border "1px solid #D9E0CE" :border-radius "999px"
    :background "transparent"
    :font-family "inherit" :font-size "12px" :font-weight "500"
    :color "#5B6F4E" :cursor "pointer"}
   [:&:hover {:border-color "#C2CDB1" :color "#2D3E2A"}]
   [:&.active
    {:background "#DCE5CE" :border-color "#C2CDB1" :color "#2D3E2A"}]]

  [:.dp-cal-nav
   {:display "flex" :align-items "center" :justify-content "space-between"
    :margin-bottom "6px"}]
  [:.dp-cal-title
   {:font-size "14px" :font-weight "600"}]
  [:.dp-nav-btn
   {:width "28px" :height "28px"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :background "transparent" :border-radius "6px"
    :font-size "18px" :line-height "1" :color "#6B7560" :cursor "pointer"}
   [:&:hover {:background "#ECEFE4" :color "#2D3E2A"}]]
  [:.dp-weekdays
   {:display "grid" :grid-template-columns "repeat(7, 1fr)"
    :margin-bottom "4px"}]
  [:.dp-weekday
   {:text-align "center" :font-size "11px" :font-weight "500"
    :color "#9AA690"}]
  [:.dp-grid
   {:display "grid" :grid-template-columns "repeat(7, 1fr)" :gap "2px"}]
  [:.dp-day
   {:aspect-ratio "1" :width "100%" :padding "0"
    :display "flex" :align-items "center" :justify-content "center"
    :border "none" :background "transparent"
    :border-radius "8px" :font-family "inherit" :font-size "13px"
    :color "#2D3E2A" :cursor "pointer"}
   [:&:hover {:background "#ECEFE4"}]
   [:&.today {:box-shadow "inset 0 0 0 1.5px #A8BA93"}]
   [:&.selected
    {:background "#2D3E2A" :color "white"}]]
  [:.dp-day-empty
   {:aspect-ratio "1"}]

  [:.dp-time-label
   {:font-size "11px" :font-weight "600" :letter-spacing "0.04em"
    :text-transform "uppercase" :color "#6B7560" :margin-bottom "6px"}]
  [:.dp-time-input
   {:width "100%" :box-sizing "border-box"
    :padding "8px 10px"
    :border "1px solid #D9E0CE" :border-radius "8px"
    :background "white"
    :font-family "inherit" :font-size "14px" :color "#2D3E2A"}
   [:&:focus {:outline "none" :border-color "#A8BA93"}]
   [:&::placeholder {:color "#9AA690"}]]
  [:.dp-slots
   {:margin-top "4px" :max-height "168px" :overflow-y "auto"
    :border "1px solid #ECEFE4" :border-radius "8px"}]
  [:.dp-slot
   {:display "block" :width "100%" :text-align "left"
    :padding "8px 10px"
    :border "none" :background "transparent"
    :font-family "inherit" :font-size "14px" :color "#2D3E2A"
    :cursor "pointer"}
   [:&:hover {:background "#ECEFE4"}]
   [:&.selected {:background "#DCE5CE" :font-weight "600"}]])

;; ---------------------------------------------------------------------------
;; Sub-components
;; ---------------------------------------------------------------------------

(def ^:private weekday-initials ["M" "T" "W" "T" "F" "S" "S"])

(defn- timed? [value] (some? (:time value)))

(defn- mode-toggle [value on-change]
  (let [datetime? (timed? value)]
    [:div.dp-toggle
     [:button.dp-toggle-opt
      {:type "button"
       :class (when-not datetime? "active")
       :on-click #(on-change (dissoc value :time))}
      "Date"]
     [:button.dp-toggle-opt
      {:type "button"
       :class (when datetime? "active")
       :on-click #(on-change (assoc value :time (or (:time value) default-time-minutes)))}
      "Date & time"]]))

(defn- quick-chips [value on-change !view]
  (let [selected (:date value)
        pick! (fn [date]
                (reset! !view (select-keys date [:year :month]))
                (on-change (assoc value :date date)))
        options [["Today" (today-date)]
                 ["Tomorrow" (add-days (today-date) 1)]
                 ["This weekend" (this-weekend-date)]]]
    [:div.dp-chips
     (for [[label date] options]
       ^{:key label}
       [:button.dp-chip
        {:type "button"
         :class (when (same-date? selected date) "active")
         :on-click #(pick! date)}
        label])]))

(defn- calendar [value on-change !view]
  (let [view @!view
        selected (:date value)
        today (today-date)]
    [:div.dp-cal
     [:div.dp-cal-nav
      [:button.dp-nav-btn
       {:type "button" :aria-label "Previous month"
        :on-click #(swap! !view shift-month -1)}
       "‹"]
      [:div.dp-cal-title (month-label view)]
      [:button.dp-nav-btn
       {:type "button" :aria-label "Next month"
        :on-click #(swap! !view shift-month 1)}
       "›"]]
     [:div.dp-weekdays
      (for [[idx initial] (map-indexed vector weekday-initials)]
        ^{:key idx} [:div.dp-weekday initial])]
     [:div.dp-grid
      (for [[idx cell] (map-indexed vector (month-cells view))]
        (if (nil? cell)
          ^{:key idx} [:div.dp-day-empty]
          (let [cell-date (assoc view :day cell)]
            ^{:key idx}
            [:button.dp-day
             {:type "button"
              :class (cond
                       (same-date? selected cell-date) "selected"
                       (same-date? today cell-date) "today")
              :on-click #(on-change (assoc value :date cell-date))}
             cell])))]]))

(defn- center-slot-in-view!
  "Center a time slot within its scrolling parent, so opening the list reveals
   the current time with earlier and later slots reachable either way."
  [slot-el]
  (let [container (.-parentNode slot-el)
        container-rect (.getBoundingClientRect container)
        slot-rect (.getBoundingClientRect slot-el)
        offset (- (.-top slot-rect) (.-top container-rect))
        gap (/ (- (.-clientHeight container) (.-offsetHeight slot-el)) 2)]
    (set! (.-scrollTop container) (- (+ (.-scrollTop container) offset) gap))))

(defn- time-combobox [value on-change]
  (r/with-let [!draft (r/atom nil)
               centered? (atom false)
               center-once! (fn [slot-el]
                              (when (and slot-el (not @centered?))
                                (reset! centered? true)
                                (center-slot-in-view! slot-el)))]
    (let [minutes (:time value)
          commit! (fn []
                    (when-let [parsed (parse-minutes @!draft)]
                      (on-change (assoc value :time parsed)))
                    (reset! !draft nil))]
      [:div.dp-time
       [:div.dp-time-label "Time"]
       [:input.dp-time-input
        {:type "text"
         :placeholder "Add a time"
         :value (or @!draft (when minutes (format-minutes minutes)) "")
         :on-change #(reset! !draft (.. % -target -value))
         :on-blur commit!
         :on-key-down (fn [e]
                        (when (= "Enter" (.-key e))
                          (.preventDefault e)
                          (commit!)
                          (.. e -target blur)))}]
       [:div.dp-slots
        (for [slot time-slots]
          ^{:key slot}
          [:button.dp-slot
           {:type "button"
            :class (when (= slot minutes) "selected")
            :ref (when (= slot minutes) center-once!)
            :on-click (fn []
                        (reset! !draft nil)
                        (on-change (assoc value :time slot)))}
           (format-minutes slot)])]])))

;; ---------------------------------------------------------------------------
;; Public component
;; ---------------------------------------------------------------------------

(defn date-picker
  "Calendar + optional time picker. `value` is the local selection map (see ns
   docstring); `on-change` receives the next value map. The mode toggle, quick
   chips and calendar can each be hidden so a caller can drive date/time itself.
   The time picker shows whenever `value` is timed."
  [{:keys [value on-change show-mode-toggle? show-quick-chips? show-calendar? show-time?]
    :or {show-mode-toggle? true show-quick-chips? true show-calendar? true}}]
  (r/with-let [!view (r/atom (or (:date value) (today-date)))]
    [frame
     (when show-mode-toggle? [mode-toggle value on-change])
     (when show-quick-chips? [quick-chips value on-change !view])
     (when show-calendar? [calendar value on-change !view])
     (when (if (some? show-time?) show-time? (timed? value))
       [time-combobox value on-change])]))
